import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, MenuItem, TextField, Typography } from '@material-ui/core'
import moment from 'moment'

import { Card, LoadingFeedback } from 'components'

import useFetch from 'hooks/useFetch'
import usePhishing from 'hooks/usePhishing'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

import constants from 'constants/index'
import helpers from 'helpers'

import { DispatchSelector } from './components'
import schema from './schema'

const { DISPATCH_IMMEDIATE, DISPATCH_SCHEDULED } = constants.phishing
const { SMTP_KIND, ACTIVE_STATUS } = constants.externalPlatformIntegrations

const PLATFORM_DEFAULT_SMTP = 'platform_default'

const ScheduleStep = () => {
  const [loading, setLoading] = useState(false)
  const [dispatchType, setDispatchType] = useState(DISPATCH_IMMEDIATE)
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null)

  const snackbar = useSnackbar()
  const {
    phishing,
    setPhishing,
    handleNext,
    isReadOnly,
    wantsToAdvanceStep,
    resetAdvanceStep,
  } = usePhishing()

  const { control, getValues, triggerValidation, setValue } = useForm({
    validationSchema: schema,
    defaultValues: {
      scheduledDate: null,
      scheduledTime: null,
    },
  })

  const { response: integrationResponse, isLoading: isIntegrationLoading } =
    useFetch(service.dponet.externalPlatformIntegrations.get, {
      externalPlatform: SMTP_KIND,
      status: ACTIVE_STATUS,
    })

  useEffect(() => {
    if (
      phishing?.scheduleType === DISPATCH_SCHEDULED ||
      phishing?.scheduleType === DISPATCH_IMMEDIATE
    ) {
      setDispatchType(phishing.scheduleType)
    }

    if (phishing?.externalPlatformIntegrationId !== undefined) {
      setSelectedIntegrationId(
        phishing.externalPlatformIntegrationId ?? PLATFORM_DEFAULT_SMTP,
      )
    }
  }, [phishing?.scheduleType, phishing?.externalPlatformIntegrationId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dispatchType !== DISPATCH_SCHEDULED || !phishing?.scheduledAt) return

    const scheduledMoment = moment(phishing.scheduledAt)
    setValue('scheduledDate', scheduledMoment)
    setValue('scheduledTime', scheduledMoment)
  }, [dispatchType]) // eslint-disable-line react-hooks/exhaustive-deps

  const smtpIntegrations =
    integrationResponse?.data?.externalPlatformIntegrations || []

  const onSubmit = async () => {
    setLoading(true)
    try {
      const isValid = await triggerValidation()

      if (!isValid) {
        resetAdvanceStep()
        return
      }

      if (selectedIntegrationId === null) {
        snackbar.open({
          message: 'Selecione um servidor SMTP para continuar.',
          variant: 'error',
        })
        resetAdvanceStep()
        return
      }

      if (selectedIntegrationId === PLATFORM_DEFAULT_SMTP) {
        const totalParticipants =
          (phishing?.recipients || []).length +
          (phishing?.externalRecipients || []).length

        if (
          totalParticipants >
          constants.phishing.PLATFORM_DEFAULT_PARTICIPANT_LIMIT
        ) {
          snackbar.open({
            message: `O servidor padrão da plataforma permite no máximo ${constants.phishing.PLATFORM_DEFAULT_PARTICIPANT_LIMIT} participantes. Reduza os participantes ou configure um SMTP próprio em Empresa → Integrações.`,
            variant: 'error',
          })
          resetAdvanceStep()
          return
        }
      }

      const { scheduledDate, scheduledTime } = getValues()

      let scheduledAt = undefined
      if (
        dispatchType === DISPATCH_SCHEDULED &&
        scheduledDate &&
        scheduledTime
      ) {
        scheduledAt = scheduledDate
          .clone()
          .hours(scheduledTime.hours())
          .minutes(scheduledTime.minutes())
          .seconds(0)
          .toISOString()
      }

      await service.dponet.phishingCampaigns.scheduleStep(phishing.id, {
        phishingCampaign: {
          scheduleType: dispatchType,
          externalPlatformIntegrationId:
            selectedIntegrationId === PLATFORM_DEFAULT_SMTP
              ? null
              : selectedIntegrationId,
          ...(scheduledAt && { scheduledAt }),
        },
      })

      setPhishing((previous) => ({
        ...previous,
        scheduleType: dispatchType,
        scheduledAt,
        scheduledDate,
        scheduledTime,
        externalPlatformIntegrationId:
          selectedIntegrationId === PLATFORM_DEFAULT_SMTP
            ? null
            : selectedIntegrationId,
      }))
      resetAdvanceStep()
      handleNext()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar agendamento da campanha.',
        variant: 'error',
      })
      resetAdvanceStep()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (wantsToAdvanceStep) {
      onSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <>
      <LoadingFeedback open={loading || isIntegrationLoading} />
      <Card>
        <Box p={2}>
          <Typography variant="h3">
            Agende uma data para o disparo dos e-mails
          </Typography>
          <Box mt={2}>
            <DispatchSelector
              dispatchType={dispatchType}
              onChange={(event) => setDispatchType(event.target.value)}
              control={control}
              disabled={isReadOnly}
            />
          </Box>
        </Box>
        <Box p={2}>
          <Typography variant="h3">
            Configurar servidor para envio de e-mail (SMTP)
          </Typography>
          <Box mt={1}>
            <Typography variant="body2" color="textSecondary">
              O servidor de envio define qual endereço de e-mail aparecerá como
              remetente nos disparos da campanha. É possível cadastrar um SMTP
              próprio em Empresa → Integrações e selecioná-lo aqui.
            </Typography>
          </Box>
          <Box mt={2}>
            <TextField
              select
              fullWidth
              label="Servidor de envio (SMTP)"
              variant="outlined"
              value={selectedIntegrationId ?? ''}
              onChange={(event) => setSelectedIntegrationId(event.target.value)}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: selectedIntegrationId !== null }}
            >
              {smtpIntegrations.map((integration) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.externalAccount}
                  {integration.additionalInfo?.address
                    ? ` (${integration.additionalInfo.address})`
                    : ''}
                </MenuItem>
              ))}
              <MenuItem value={PLATFORM_DEFAULT_SMTP}>
                Padrão da plataforma
              </MenuItem>
            </TextField>
          </Box>
        </Box>
      </Card>
    </>
  )
}

export default ScheduleStep
